import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	OnInit,
	computed,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldAppearance, MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PricePipe } from '@/common/pipes/price.pipe';
import { OfferService, Slot } from '../../../../../../reservation.model';
import { getAutocompleteServices } from '../_utils/reservation-services-autocomplete.util';
import {
	getReservationSlotServicesSum,
	getReservationSlotTotalPrice,
} from '../_utils/reservation-slot-service-pricing.util';
import { startWith } from 'rxjs';

@Component({
	selector: 'app-reservation-services-dialog-participant',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatDividerModule,
		MatCardModule,
		PricePipe,
	],
	templateUrl: './reservation-services-dialog-participant.component.html',
	styleUrls: ['./reservation-services-dialog-participant.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationServicesDialogParticipantComponent implements OnInit {
	readonly slotIndex = input.required<number>();
	readonly slot = input.required<Slot>();
	readonly initialServicesSum = input.required<number>();
	readonly initialPriceAdjustment = input.required<number>();
	readonly allServices = input.required<OfferService[]>();
	readonly formFieldAppearance = input.required<MatFormFieldAppearance>();
	readonly formFieldSubscriptSizing = input.required<SubscriptSizing>();
	readonly serviceControl = input.required<FormControl<string>>();

	readonly removeService = output<number>();
	readonly addService = output<string>();
	readonly priceAdjustmentChange = output<number>();

	readonly selectedServiceName = signal<string | null>(null);
	readonly searchQuery = signal('');
	readonly autocompleteOptions = computed(() =>
		getAutocompleteServices({
			allServices: this.allServices(),
			slots: [this.slot()],
			mode: 'not-on-slot',
			slot: this.slot(),
			searchQuery: this.searchQuery(),
		}),
	);
	readonly servicesCount = computed(() => this.slot().slotServices?.length ?? 0);
	readonly servicesSum = computed(() => getReservationSlotServicesSum(this.slot()));
	readonly participantTotalPrice = computed(() =>
		getReservationSlotTotalPrice(this.slot(), this.initialServicesSum(), this.initialPriceAdjustment()),
	);

	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		const control = this.serviceControl();

		control.valueChanges.pipe(startWith(control.value), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			this.searchQuery.set(value);
			this.selectedServiceName.set(null);
		});
	}

	onServiceSelected(event: MatAutocompleteSelectedEvent): void {
		this.selectedServiceName.set(event.option.value);
	}

	onAddService(): void {
		const selectedServiceName = this.selectedServiceName();
		if (!selectedServiceName) {
			return;
		}

		this.addService.emit(selectedServiceName);
	}

	onRemoveService(serviceIndex: number): void {
		this.removeService.emit(serviceIndex);
	}

	onPriceAdjustmentChange(value: number | null): void {
		if (value == null) {
			this.priceAdjustmentChange.emit(0);
			return;
		}

		this.priceAdjustmentChange.emit(this.convertPriceToGrosze(value));
	}

	convertPriceToGrosze(priceInPln: number): number {
		return priceInPln > 0 ? Math.ceil(priceInPln * 100) : Math.floor(priceInPln * 100);
	}
}
